import React, { useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent } from "react";

import type { LandingPageContent, SeoInfoCardContent, SeoInfoCardFace } from "../content/types";

type SeoInfoCardsProps = {
  content: LandingPageContent["seoInfoCards"];
};

const SEO_INFO_CARD_FRONT_FACE: SeoInfoCardFace = "front";
const SEO_INFO_CARD_BACK_FACE: SeoInfoCardFace = "back";

export function SeoInfoCards({ content }: SeoInfoCardsProps) {
  const [activeId, setActiveId] = useState<SeoInfoCardContent["id"] | null>(null);
  const pointerFocusRef = useRef(false);
  const lastTriggerPointerTypeRef = useRef<PointerEvent<HTMLButtonElement>["pointerType"] | null>(null);
  const itemsById = new Map(content.items.map((item) => [item.id, item]));

  function closeWhenFocusLeaves(event: FocusEvent<HTMLElement>) {
    const nextTarget = event.relatedTarget;
    if (!nextTarget || !event.currentTarget.contains(nextTarget as Node)) {
      setActiveId(null);
    }
  }

  function openForMousePointer(event: PointerEvent<HTMLElement>, id: SeoInfoCardContent["id"]) {
    if (event.pointerType === "mouse") {
      setActiveId(id);
    }
  }

  function closeWhenPointerLeaves(event: PointerEvent<HTMLElement>) {
    if (event.pointerType !== "mouse") {
      return;
    }

    const activeElement = event.currentTarget.ownerDocument.activeElement;
    if (!activeElement || !event.currentTarget.contains(activeElement)) {
      setActiveId(null);
    }
  }

  function closeWithEscape(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== "Escape") {
      return;
    }

    event.stopPropagation();
    setActiveId(null);
    event.currentTarget.querySelector("button")?.focus();
  }

  function rememberTriggerPointer(event: PointerEvent<HTMLButtonElement>) {
    pointerFocusRef.current = true;
    lastTriggerPointerTypeRef.current = event.pointerType;
  }

  function resetTriggerPointer() {
    pointerFocusRef.current = false;
    lastTriggerPointerTypeRef.current = null;
  }

  function toggleFromTriggerClick(id: SeoInfoCardContent["id"]) {
    const pointerType = lastTriggerPointerTypeRef.current;
    pointerFocusRef.current = false;
    lastTriggerPointerTypeRef.current = null;

    if (pointerType === "mouse") {
      setActiveId(id);
      return;
    }

    setActiveId((current) => (current === id ? null : id));
  }

  function renderSeoInfoCard(item: SeoInfoCardContent) {
    const isActive = activeId === item.id;
    const panelId = `seo-info-panel-${item.id}`;
    const titleId = `seo-info-title-${item.id}`;
    const frontAssetId = item.frontAssetId;

    return (
      <article
        className={`seo-info-card${isActive ? " is-active" : ""}`}
        data-seo-info-card
        data-topic={item.id}
        key={item.id}
        onBlur={closeWhenFocusLeaves}
        onFocus={() => {
          if (pointerFocusRef.current) {
            pointerFocusRef.current = false;
            return;
          }

          setActiveId(item.id);
        }}
        onKeyDown={closeWithEscape}
        onPointerEnter={(event) => openForMousePointer(event, item.id)}
        onPointerLeave={closeWhenPointerLeaves}
      >
        <button
          className="seo-info-card__trigger"
          type="button"
          aria-controls={panelId}
          aria-expanded={isActive}
          onClick={() => toggleFromTriggerClick(item.id)}
          onPointerCancel={resetTriggerPointer}
          onPointerDown={rememberTriggerPointer}
        >
          <span className="seo-info-card__title-row">
            <span className="seo-info-card__title" id={titleId}>
              {item.title}
            </span>
            <span className="seo-info-card__affordance" aria-hidden="true" data-info-affordance />
          </span>
          <span className="seo-info-card__summary">{item.body}</span>
          <span className="seo-info-card__keyword">{item.keyword}</span>
        </button>

        <div className="seo-info-card__visual-shell" data-seo-card-visual>
          <div className="seo-info-card__visual-inner">
            <figure
              className="seo-info-card__visual-face seo-info-card__visual-face--front"
              aria-hidden="true"
              data-card-face={SEO_INFO_CARD_FRONT_FACE}
              data-card-asset={frontAssetId}
            >
              <img
                src={`/assets/dynamicbooks-v3/${frontAssetId}.svg`}
                alt=""
                loading="lazy"
                decoding="async"
                aria-hidden="true"
              />
            </figure>

            <div
              className="seo-info-card__details seo-info-card__visual-face seo-info-card__visual-face--back"
              id={panelId}
              role="region"
              aria-hidden={!isActive}
              aria-labelledby={titleId}
              data-card-face={SEO_INFO_CARD_BACK_FACE}
              data-card-asset={item.assetId}
            >
              <img
                src={`/assets/dynamicbooks-v3/${item.assetId}.svg`}
                alt=""
                loading="lazy"
                decoding="async"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <section className="section-band seo-info-section" id={content.id} aria-labelledby="seo-info-title">
      <div className="db-container db-section">
        <div className="section-heading">
          <p className="section-eyebrow">{content.eyebrow}</p>
          <h2 id="seo-info-title">{content.title}</h2>
          <p>{content.lead}</p>
        </div>

        <div className="seo-info-grid" data-seo-info-grid>
          {content.groups.map((group) => {
            const groupTitleId = `seo-info-group-${group.id}`;
            const groupItems = group.itemIds
              .map((itemId) => itemsById.get(itemId))
              .filter((item): item is SeoInfoCardContent => item !== undefined);

            return (
              <section
                className="seo-info-group"
                data-seo-info-group={group.id}
                key={group.id}
                aria-labelledby={groupTitleId}
              >
                <h3 className="seo-info-group__title" id={groupTitleId}>
                  {group.title}
                </h3>
                <div className="seo-info-group__cards">{groupItems.map(renderSeoInfoCard)}</div>
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
}
